import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PersonAvatarWithIndicators, PersonSelectRow } from './PersonAvatarWithIndicators';
import { fetchPersonActivityMapsForPeopleList } from '../utils/personActivityCounts';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import axios from 'axios';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { toast } from 'sonner';
import { 
  Dna,
  Upload,
  User,
  Shield,
  Lock,
  Trash2,
  Link2,
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  Loader2,
  FileText,
  Globe,
  ChevronDown,
  ChevronUp,
  Settings,
  Eye,
  EyeOff,
  X,
  HelpCircle
} from 'lucide-react';
import DNAInsightsPanel from './DNAInsightsPanel';
import DNAMatchesPanel from './DNAMatchesPanel';
import { OnboardingBanner } from './onboarding';
import { DnaUploadResultCard } from './dna/DnaUploadResultCard';
import {
  DNA_UPLOAD_ACCEPT_ATTR,
  DNA_UPLOAD_FILE_WARNING,
  DNA_UPLOAD_INTRO,
  DNA_UPLOAD_LEGACY_23ANDME_NOTE,
  DNA_UPLOAD_PRIVACY_NOTE,
  DNA_UPLOAD_SUPPORTED_PROVIDERS,
  DNA_UPLOAD_TITLE,
} from '../utils/dnaUploadCopy';
import { guessDnaProviderFromFilename, validateDnaUploadFile } from '../utils/dnaUploadValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// DNA Source labels
const sourceLabels = {
  ancestry: 'AncestryDNA',
  '23andme': '23andMe',
  myheritage: 'MyHeritage',
  ftdna: 'Family Tree DNA',
  gedmatch: 'GEDmatch',
  other: 'Living DNA / other',
  user_uploaded: 'Custom Upload',
  unknown: 'Unknown Source'
};

// Consent status badges
const ConsentBadge = ({ status }) => {
  const styles = {
    granted: 'bg-primary/10 text-primary border-primary/30',
    pending: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    revoked: 'bg-destructive/10 text-destructive border-destructive/30'
  };
  
  const labels = {
    granted: 'Consent Given',
    pending: 'Consent Pending',
    revoked: 'Consent Revoked'
  };
  
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider font-medium ${styles[status] || styles.pending}`}>
      {labels[status] || 'Unknown'}
    </span>
  );
};

// Ethnicity bar chart
const EthnicityEstimates = ({ estimates }) => {
  if (!estimates || estimates.length === 0) return null;
  
  // Sort by percentage descending
  const sorted = [...estimates].sort((a, b) => (b.percentage || 0) - (a.percentage || 0));
  
  return (
    <div className="space-y-2">
      {sorted.slice(0, 5).map((est, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-foreground">{est.region}</span>
            <span className="text-muted-foreground font-medium">{Math.round(est.percentage)}%</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${est.percentage}%` }}
            />
          </div>
        </div>
      ))}
      {sorted.length > 5 && (
        <p className="text-[10px] text-muted-foreground italic">
          + {sorted.length - 5} more regions
        </p>
      )}
    </div>
  );
};

// Haplogroup display
const HaplogroupDisplay = ({ haplogroup }) => {
  if (!haplogroup) return null;
  
  return (
    <div className="flex gap-4">
      {haplogroup.maternal && (
        <div className="flex-1 p-3 rounded-lg bg-secondary/60 border border-border">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 font-medium">Maternal Line</p>
          <p className="text-sm font-mono text-foreground font-medium">{haplogroup.maternal}</p>
        </div>
      )}
      {haplogroup.paternal && (
        <div className="flex-1 p-3 rounded-lg bg-secondary/60 border border-border">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1 font-medium">Paternal Line</p>
          <p className="text-sm font-mono text-foreground font-medium">{haplogroup.paternal}</p>
        </div>
      )}
    </div>
  );
};

// Single DNA Profile Card
const DNAProfileCard = ({ profile, allPeople, onUpdate, onDelete, onLink, expandDetails = false }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState(profile.person_id || '');
  const [selectedTreeId, setSelectedTreeId] = useState(profile.tree_id || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [linkStoryCounts, setLinkStoryCounts] = useState({});
  const [linkDocumentCounts, setLinkDocumentCounts] = useState({});

  const dnaPeopleKey = useMemo(
    () => (allPeople || []).map((p) => `${p.id}:${p.tree_id || ''}`).sort().join('|'),
    [allPeople]
  );

  useEffect(() => {
    if (!showLinkDialog || !allPeople?.length) {
      setLinkStoryCounts({});
      setLinkDocumentCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { storyCounts, documentCounts } = await fetchPersonActivityMapsForPeopleList(
          BACKEND_URL,
          allPeople
        );
        if (!cancelled) {
          setLinkStoryCounts(storyCounts);
          setLinkDocumentCounts(documentCounts);
        }
      } catch {
        if (!cancelled) {
          setLinkStoryCounts({});
          setLinkDocumentCounts({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allPeople covered by dnaPeopleKey
  }, [showLinkDialog, dnaPeopleKey]);

  useEffect(() => {
    if (expandDetails) setShowDetails(true);
  }, [expandDetails]);
  
  const handleConsentToggle = async (enabled) => {
    setIsUpdating(true);
    try {
      await axios.patch(`${API}/dna/profiles/${profile.profile_id}/consent`, {
        consent_granted: enabled,
        matching_enabled: enabled ? profile.matching_enabled : false
      });
      
      onUpdate();
      toast.success(enabled ? 'Consent granted' : 'Consent revoked', { duration: 2000 });
    } catch (error) {
      console.error('Consent update error:', error);
      toast.error('Unable to update consent');
    } finally {
      setIsUpdating(false);
    }
  };
  
  const handleMatchingToggle = async (enabled) => {
    if (profile.consent_status !== 'granted') {
      toast.error('Please grant consent before enabling matching');
      return;
    }
    
    setIsUpdating(true);
    try {
      await axios.patch(`${API}/dna/profiles/${profile.profile_id}/consent`, {
        consent_granted: true,
        matching_enabled: enabled
      });
      
      onUpdate();
      toast.success(enabled ? 'DNA matching enabled' : 'DNA matching disabled', { duration: 2000 });
    } catch (error) {
      console.error('Matching update error:', error);
      toast.error('Unable to update matching preference');
    } finally {
      setIsUpdating(false);
    }
  };
  
  const handleDelete = async () => {
    setIsUpdating(true);
    try {
      await axios.delete(`${API}/dna/profiles/${profile.profile_id}`);
      onDelete(profile.profile_id);
      toast.success('DNA profile deleted', { duration: 2000 });
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Unable to delete profile');
    } finally {
      setIsUpdating(false);
      setShowDeleteDialog(false);
    }
  };
  
  const handleLink = async () => {
    if (!selectedPersonId || !selectedTreeId) {
      toast.error('Please select a person to link');
      return;
    }
    
    setIsUpdating(true);
    try {
      await axios.patch(`${API}/dna/profiles/${profile.profile_id}/link?person_id=${selectedPersonId}&tree_id=${selectedTreeId}`);
      onUpdate();
      toast.success('DNA profile linked to family member', { duration: 2000 });
      setShowLinkDialog(false);
    } catch (error) {
      console.error('Link error:', error);
      toast.error('Unable to link profile');
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Find linked person name
  const linkedPerson = allPeople.find(p => p.id === profile.person_id);
  const linkedPersonName = linkedPerson 
    ? `${linkedPerson.given_names || ''} ${linkedPerson.surname || ''}`.trim()
    : null;
  
  return (
    <>
      <Card className="bg-card border-border shadow-sm" data-testid={`dna-profile-card-${profile.profile_id}`}>
        <CardContent className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <Dna className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {sourceLabels[profile.source] || 'DNA Profile'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Uploaded {new Date(profile.uploaded_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            <ConsentBadge status={profile.consent_status} />
          </div>
          
          {/* Linked Person */}
          {linkedPersonName ? (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/60 border border-border">
              <User className="w-4 h-4 text-primary" />
              <span className="text-sm text-foreground">Linked to {linkedPersonName}</span>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLinkDialog(true)}
              className="w-full h-9 text-xs border-border text-foreground hover:bg-secondary"
              data-testid={`link-dna-btn-${profile.profile_id}`}
            >
              <Link2 className="w-3.5 h-3.5 mr-1.5" />
              Link to Family Member
            </Button>
          )}
          
          {/* Toggle details */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors py-1 font-medium"
          >
            <span>{showDetails ? 'Hide Details' : 'Show Details'}</span>
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {/* Expanded details */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-4 overflow-hidden"
              >
                {/* Haplogroups */}
                {profile.haplogroup && (
                  <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Haplogroups</p>
                    <HaplogroupDisplay haplogroup={profile.haplogroup} />
                  </div>
                )}
                
                {/* Ethnicity estimates */}
                {profile.ethnicity_estimates && profile.ethnicity_estimates.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Heritage Estimates</p>
                    <EthnicityEstimates estimates={profile.ethnicity_estimates} />
                  </div>
                )}
                
                {/* No data message */}
                {!profile.haplogroup && (!profile.ethnicity_estimates || profile.ethnicity_estimates.length === 0) && (
                  <div className="text-center py-4">
                    <Info className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">
                      Detailed analysis will be available when DNA matching is enabled.
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Privacy controls */}
          <div className="space-y-3 pt-3 border-t border-border" data-testid="dna-privacy-section">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider flex items-center gap-1 font-medium">
              <Shield className="w-3 h-3" />
              Privacy Controls
            </p>
            
            {/* Consent toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-foreground">Storage consent</span>
              </div>
              <Switch
                checked={profile.consent_status === 'granted'}
                onCheckedChange={handleConsentToggle}
                disabled={isUpdating}
                data-testid={`consent-toggle-${profile.profile_id}`}
              />
            </div>
            
            {/* Matching toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Dna className="w-4 h-4 text-muted-foreground" />
                <div>
                  <span className="text-sm text-foreground">DNA Matching</span>
                  <p className="text-[10px] text-muted-foreground">Optional • Always under your control</p>
                </div>
              </div>
              <Switch
                checked={profile.matching_enabled}
                onCheckedChange={handleMatchingToggle}
                disabled={isUpdating || profile.consent_status !== 'granted'}
                data-testid={`matching-toggle-${profile.profile_id}`}
              />
            </div>
            
            {/* Matching status */}
            {profile.matching_enabled ? (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <CheckCircle className="w-4 h-4 text-primary" />
                <p className="text-xs text-foreground">
                  Matching enabled. No matches found yet.
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary/60 border border-border">
                <EyeOff className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  Matching is off. Your DNA data is private.
                </p>
              </div>
            )}
          </div>
          
          {/* Delete button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            disabled={isUpdating}
            className="w-full h-9 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid={`delete-dna-btn-${profile.profile_id}`}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Delete DNA Profile
          </Button>
        </CardContent>
      </Card>
      
      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground font-serif">Delete DNA Profile?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Your DNA data will be permanently removed from our systems. This cannot be undone. 
              Any links to family members will be removed. 
              You can always upload again later if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border text-foreground hover:bg-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Link to person dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-serif">Link DNA to Family Member</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Connect this DNA profile to someone in your family tree.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-foreground font-medium">Select Family Member</Label>
              <Select value={selectedPersonId} onValueChange={(val) => {
                setSelectedPersonId(val);
                const person = allPeople.find(p => p.id === val);
                if (person) setSelectedTreeId(person.tree_id);
              }}>
                <SelectTrigger className="bg-background border-input text-foreground h-auto min-h-10 py-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {selectedPersonId && (() => {
                      const p = allPeople.find((x) => x.id === selectedPersonId);
                      if (!p) return null;
                      return (
                        <PersonAvatarWithIndicators
                          photoUrl={
                            p.profile_image_url ? resolveAssetUrl(BACKEND_URL, p.profile_image_url) : null
                          }
                          initials={`${p.given_names?.charAt(0) || ''}${p.surname?.charAt(0) || ''}`}
                          size="xs"
                          storyCount={linkStoryCounts[p.id] ?? 0}
                          documentCount={linkDocumentCounts[p.id] ?? 0}
                        />
                      );
                    })()}
                    <SelectValue placeholder="Choose a person..." className="truncate text-left" />
                  </div>
                </SelectTrigger>
                <SelectContent className="bg-popover border-border max-h-[min(60vh,320px)]">
                  {allPeople.map((person) => (
                    <SelectItem
                      key={person.id}
                      value={person.id}
                      className="text-popover-foreground py-2 pl-2 pr-8"
                      textValue={
                        `${person.given_names || ''} ${person.surname || ''}`.trim() || 'Unknown'
                      }
                    >
                      <PersonSelectRow
                        person={person}
                        photoBaseUrl={BACKEND_URL}
                        size="sm"
                        storyCount={linkStoryCounts[person.id] ?? 0}
                        documentCount={linkDocumentCounts[person.id] ?? 0}
                      />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <p className="text-xs text-muted-foreground italic">
              Linking DNA helps trace ancestral connections through your family tree.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowLinkDialog(false)}
              className="border-border text-foreground hover:bg-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLink}
              disabled={!selectedPersonId || isUpdating}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isUpdating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Link2 className="w-4 h-4 mr-2" />
              )}
              Link Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Upload DNA Dialog
const UploadDNADialog = ({ open, onClose, allPeople, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [source, setSource] = useState('unknown');
  const [personId, setPersonId] = useState('none');
  const [treeId, setTreeId] = useState('');
  const [consentGranted, setConsentGranted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [uploadStoryCounts, setUploadStoryCounts] = useState({});
  const [uploadDocumentCounts, setUploadDocumentCounts] = useState({});

  const uploadPeopleKey = useMemo(
    () => (allPeople || []).map((p) => `${p.id}:${p.tree_id || ''}`).sort().join('|'),
    [allPeople]
  );

  useEffect(() => {
    if (!open || !allPeople?.length) {
      setUploadStoryCounts({});
      setUploadDocumentCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { storyCounts, documentCounts } = await fetchPersonActivityMapsForPeopleList(
          BACKEND_URL,
          allPeople
        );
        if (!cancelled) {
          setUploadStoryCounts(storyCounts);
          setUploadDocumentCounts(documentCounts);
        }
      } catch {
        if (!cancelled) {
          setUploadStoryCounts({});
          setUploadDocumentCounts({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allPeople covered by uploadPeopleKey
  }, [open, uploadPeopleKey]);
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validation = validateDnaUploadFile(selectedFile);
    if (!validation.ok) {
      toast.error(validation.error);
      e.target.value = '';
      return;
    }

    setFile(selectedFile);
    setSource(guessDnaProviderFromFilename(selectedFile.name));
  };
  
  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }
    
    if (!consentGranted) {
      toast.error('Please provide consent to store your DNA data');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source', source);
      formData.append('consent_granted', 'true');
      if (personId && personId !== 'none') formData.append('person_id', personId);
      if (treeId) formData.append('tree_id', treeId);
      
      const response = await axios.post(`${API}/dna/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      onUploadSuccess(response.data.profile_id);
      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.detail || 'Unable to upload DNA file';
      toast.error(message);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  const handleClose = () => {
    setFile(null);
    setSource('unknown');
    setPersonId('none');
    setTreeId('');
    setConsentGranted(false);
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground flex flex-wrap items-center gap-2 font-serif">
            <Dna className="w-5 h-5 text-primary" />
            {DNA_UPLOAD_TITLE}
            <span className="rounded-full border border-amber-600/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-sans font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
              Beta
            </span>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground leading-relaxed">
            {DNA_UPLOAD_INTRO}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <div className="rounded-lg border border-border/60 bg-secondary/30 px-4 py-3 space-y-2">
            <p className="text-xs font-medium text-foreground">Supported examples</p>
            <ul className="grid gap-1 sm:grid-cols-2 text-xs text-muted-foreground">
              {DNA_UPLOAD_SUPPORTED_PROVIDERS.map((name) => (
                <li key={name} className="flex items-center gap-1.5">
                  <span className="text-primary/70" aria-hidden>
                    ·
                  </span>
                  {name}
                </li>
              ))}
            </ul>
            <p className="text-[11px] leading-relaxed text-muted-foreground/90 pt-1 border-t border-border/40">
              {DNA_UPLOAD_LEGACY_23ANDME_NOTE}
            </p>
          </div>

          {/* File upload area */}
          <div className="space-y-2">
            <Label className="text-sm text-foreground font-medium">Raw DNA data file</Label>
            <div 
              className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                file 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:border-primary/50 hover:bg-secondary/30'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={DNA_UPLOAD_ACCEPT_ATTR}
                onChange={handleFileChange}
                className="hidden"
                data-testid="dna-file-input"
              />
              
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-primary" />
                  <div className="text-left">
                    <p className="text-sm text-foreground font-medium">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                    className="h-8 w-8 p-0 hover:bg-secondary"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-foreground">Click to choose a file</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    .zip, .txt, or .csv from your genealogy DNA provider
                  </p>
                </>
              )}
            </div>
            <div className="flex items-start gap-2 rounded-lg border border-amber-700/25 bg-amber-500/5 px-3 py-2.5">
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-700 dark:text-amber-400 mt-0.5" aria-hidden />
              <p className="text-[11px] leading-relaxed text-muted-foreground">{DNA_UPLOAD_FILE_WARNING}</p>
            </div>
          </div>
          
          {/* Source selector */}
          <div className="space-y-2">
            <Label className="text-sm text-foreground font-medium">DNA Provider</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="bg-background border-input text-foreground" data-testid="dna-source-select">
                <SelectValue placeholder="Select provider..." />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="ancestry" className="text-popover-foreground">AncestryDNA</SelectItem>
                <SelectItem value="myheritage" className="text-popover-foreground">MyHeritage</SelectItem>
                <SelectItem value="ftdna" className="text-popover-foreground">FamilyTreeDNA</SelectItem>
                <SelectItem value="other" className="text-popover-foreground">Living DNA</SelectItem>
                <SelectItem value="gedmatch" className="text-popover-foreground">GEDmatch</SelectItem>
                <SelectItem value="unknown" className="text-popover-foreground">Other compatible raw DNA file</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Link to person (optional) */}
          <div className="space-y-2">
            <Label className="text-sm text-foreground font-medium">
              Link to Family Member <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Select value={personId} onValueChange={(val) => {
              setPersonId(val);
              const person = allPeople.find(p => p.id === val);
              if (person) setTreeId(person.tree_id);
            }}>
              <SelectTrigger
                className="bg-background border-input text-foreground h-auto min-h-10 py-2"
                data-testid="dna-person-select"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {personId && personId !== 'none' && (() => {
                    const p = allPeople.find((x) => x.id === personId);
                    if (!p) return null;
                    return (
                      <PersonAvatarWithIndicators
                        photoUrl={
                          p.profile_image_url ? resolveAssetUrl(BACKEND_URL, p.profile_image_url) : null
                        }
                        initials={`${p.given_names?.charAt(0) || ''}${p.surname?.charAt(0) || ''}`}
                        size="xs"
                        storyCount={uploadStoryCounts[p.id] ?? 0}
                        documentCount={uploadDocumentCounts[p.id] ?? 0}
                      />
                    );
                  })()}
                  <SelectValue placeholder="Choose a person..." className="truncate text-left" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-popover border-border max-h-[min(60vh,320px)]">
                <SelectItem value="none" className="text-popover-foreground">
                  None (link later)
                </SelectItem>
                {allPeople.map((person) => (
                  <SelectItem
                    key={person.id}
                    value={person.id}
                    className="text-popover-foreground py-2 pl-2 pr-8"
                    textValue={
                      `${person.given_names || ''} ${person.surname || ''}`.trim() || 'Unknown'
                    }
                  >
                    <PersonSelectRow
                      person={person}
                      photoBaseUrl={BACKEND_URL}
                      size="sm"
                      storyCount={uploadStoryCounts[person.id] ?? 0}
                      documentCount={uploadDocumentCounts[person.id] ?? 0}
                    />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Consent — plain language, genealogy-focused */}
          <div className="p-4 rounded-lg bg-secondary/50 border border-border space-y-3">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div className="space-y-2 flex-1">
                <p className="text-sm text-foreground font-medium">Before you upload</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {DNA_UPLOAD_PRIVACY_NOTE} Your data is for genealogical exploration only — not health or
                  medical interpretation. Matching is optional. You can delete your file anytime.
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={consentGranted}
                onCheckedChange={setConsentGranted}
                data-testid="dna-consent-checkbox"
              />
              <Label className="text-sm text-foreground cursor-pointer" onClick={() => setConsentGranted(!consentGranted)}>
                I understand and agree to store my DNA data here
              </Label>
            </div>
          </div>
          
          {/* Upload progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}
        </div>
        
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
            className="border-border text-foreground hover:bg-secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || !consentGranted || isUploading}
            className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            data-testid="dna-upload-btn"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Preserve DNA Profile
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main DNAProfilesPanel
const DNAProfilesPanel = ({ allPeople = [] }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadResultProfile, setUploadResultProfile] = useState(null);
  const [focusProfileId, setFocusProfileId] = useState(null);
  
  // Fetch profiles
  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/dna/profiles`);
      setProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Error fetching DNA profiles:', error);
      toast.error('Unable to load DNA profiles');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchProfiles();
  }, []);
  
  const handleUploadSuccess = async (profileId) => {
    await fetchProfiles();
    if (!profileId) return;
    try {
      const response = await axios.get(`${API}/dna/profiles/${profileId}`);
      setUploadResultProfile(response.data);
    } catch (error) {
      console.error('Error loading upload result:', error);
      toast.success('DNA file stored privately in your archive', { duration: 3000 });
    }
  };
  
  const handleUpdate = () => {
    fetchProfiles();
  };
  
  const handleDelete = (profileId) => {
    setProfiles(prev => prev.filter(p => p.profile_id !== profileId));
    if (focusProfileId === profileId) setFocusProfileId(null);
  };

  const focusUploadedProfile = (profileId, { privacySection = false } = {}) => {
    setUploadResultProfile(null);
    setFocusProfileId(profileId);
    window.setTimeout(() => {
      const el = document.querySelector(`[data-testid="dna-profile-card-${profileId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (privacySection) {
        el?.querySelector('[data-testid="dna-privacy-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 150);
  };
  
  return (
    <div className="space-y-6" data-testid="dna-profiles-panel">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-serif font-medium text-foreground flex items-center gap-2">
            <Dna className="w-5 h-5 text-primary" />
            DNA Preservation
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Securely store and manage DNA profiles for your family.
          </p>
        </div>
        <Button
          onClick={() => setShowUploadDialog(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          data-testid="upload-dna-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload DNA
        </Button>
      </div>
      
      {/* Privacy notice - clear, reassuring */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
        <Shield className="w-5 h-5 text-primary mt-0.5 shrink-0" />
        <div>
          <p className="text-sm text-foreground font-medium">Your DNA data stays yours</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            We use your DNA only for genealogical exploration—never for health or medical purposes. 
            Matching is optional and you control it. You can delete your data anytime. 
            We do not sell or share your family information.
          </p>
        </div>
      </div>
      
      {/* Profiles list */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-primary animate-spin mb-2" />
          <p className="text-sm text-muted-foreground">Loading DNA profiles...</p>
        </div>
      ) : profiles.length > 0 ? (
        <div className="space-y-6">
          <OnboardingBanner id="explore-dna">
            Your DNA can reveal heritage connections. Explore ethnicity and lineage insights below, or open your family tree for the full Insights tab.
          </OnboardingBanner>
          <div className="space-y-4">
            {profiles.map((profile) => (
              <DNAProfileCard
                key={profile.profile_id}
                profile={profile}
                allPeople={allPeople}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onLink={handleUpdate}
                expandDetails={focusProfileId === profile.profile_id}
            />
          ))}
          </div>
          {/* Heritage Insights - premium panel */}
          <DNAInsightsPanel profiles={profiles} />
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
            <Dna className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-serif font-medium text-foreground mb-2">No DNA Profiles Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
            Upload raw autosomal DNA from a genealogy provider to help test future matching features.
            AncestryDNA, MyHeritage, FamilyTreeDNA, and other compatible raw files are supported.
          </p>
          <Button
            onClick={() => setShowUploadDialog(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Your First DNA Profile
          </Button>
        </div>
      )}
      
      {/* DNA Matches Panel - shows when profiles exist with matching enabled */}
      {profiles.some(p => p.matching_enabled) && (
        <DNAMatchesPanel />
      )}
      
      {/* Upload dialog */}
      <UploadDNADialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        allPeople={allPeople}
        onUploadSuccess={handleUploadSuccess}
      />

      <Dialog
        open={Boolean(uploadResultProfile)}
        onOpenChange={(open) => {
          if (!open) setUploadResultProfile(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-card sm:max-w-lg">
          <DnaUploadResultCard
            profile={uploadResultProfile}
            onDismiss={() => setUploadResultProfile(null)}
            onManageProfile={() => {
              if (uploadResultProfile?.profile_id) {
                focusUploadedProfile(uploadResultProfile.profile_id);
              }
            }}
            onManagePrivacy={() => {
              if (uploadResultProfile?.profile_id) {
                focusUploadedProfile(uploadResultProfile.profile_id, { privacySection: true });
              }
            }}
            onUploadAnother={() => {
              setUploadResultProfile(null);
              setShowUploadDialog(true);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DNAProfilesPanel;
